import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import {
  changeContractStatus,
  listContracts,
  listCustomers,
  listSectors,
  listTariffs,
  updateContract,
  updateMeter,
  type ContractListItem,
  type CustomerListItem,
  type SectorItem,
  type TariffOption,
} from "@/lib/api";
import NuevoContratoModal, { type NewContractPayload } from "../clientes/components/NuevoContratoModal";

type EstadoFilter = "todos" | "active" | "inactive";
type TipoFilter = "todos" | "domiciliario" | "empresa" | "institucion";

type Row = {
  id: string;
  contractNumber: string;
  cliente: string;
  rut: string;
  customerType: "domiciliario" | "empresa" | "institucion";
  direccion: string;
  sector: string;
  medidor: string;
  tariffName: string;
  contractDate: string;
  status: string;
  meterId: string;
  tipoMedidor: string;
  selloMedidor: string;
  billingDocumentType: "39" | "41" | "33" | "34";
  sectorId: string;
  tariffId: string;
};

const estadoBadge: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-gray-100 text-gray-600",
};

const estadoLabel: Record<string, string> = {
  active: "Vigente",
  inactive: "Inactivo",
};

const tipoLabel: Record<string, string> = {
  domiciliario: "Residencial",
  empresa: "Comercial",
  institucion: "Institucional",
};

function normalizeBillingDocType(v?: string): "39" | "41" | "33" | "34" {
  if (v === "41" || v === "33" || v === "34") return v;
  return "39";
}

export default function ContratosPage() {
  const navigate = useNavigate();
  const { session } = useSession();

  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState<EstadoFilter>("todos");
  const [tipoFilter, setTipoFilter] = useState<TipoFilter>("todos");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [tariffOptions, setTariffOptions] = useState<TariffOption[]>([]);
  const [sectorOptions, setSectorOptions] = useState<SectorItem[]>([]);
  const [editingRow, setEditingRow] = useState<Row | null>(null);

  async function loadRows(): Promise<void> {
    if (!session) return;
    setLoading(true);
    setError("");
    try {
      const [contracts, customers] = await Promise.all([listContracts(session), listCustomers(session)]);
      const customerById = new Map<string, CustomerListItem>();
      customers.forEach((customer) => customerById.set(customer.id, customer));

      const nextRows: Row[] = contracts.map((contract: ContractListItem) => {
        const customer = customerById.get(contract.customerId);
        return {
          id: contract.id,
          contractNumber: contract.contractNumber,
          cliente: customer?.nombre ?? "Sin cliente",
          rut: customer?.rut ?? "-",
          customerType: customer?.customerType ?? "domiciliario",
          direccion: contract.startAddress,
          sector: contract.sector || "-",
          medidor: contract.meterSerialNumber || contract.meterId,
          tariffName: contract.tariffName || "-",
          contractDate: contract.contractDate,
          status: contract.status,
          meterId: contract.meterId,
          tipoMedidor: contract.meterType || "Mecanico",
          selloMedidor: contract.meterSealNumber || "",
          billingDocumentType: normalizeBillingDocType(contract.billingDocumentType),
          sectorId: contract.sectorId ?? "",
          tariffId: "",
        };
      });

      setRows(nextRows);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!session) return;
    void loadRows();
    void Promise.all([listTariffs(session), listSectors(session)]).then(([t, s]) => {
      setTariffOptions(t);
      setSectorOptions(s);
    });
  }, [session]);

  async function handleEditContract(payload: NewContractPayload): Promise<void> {
    if (!session || !editingRow) return;
    const matchedTariff = tariffOptions.find((t) => t.id === payload.tariffId) ?? tariffOptions[0];
    if (!matchedTariff) throw new Error("No hay tarifas disponibles");
    const matchedSector = sectorOptions.find((s) => s.id === payload.sectorId) ?? sectorOptions[0];
    if (!matchedSector) throw new Error("No hay sectores disponibles");

    await updateContract(session, editingRow.id, {
      meterId: editingRow.meterId,
      tariffId: matchedTariff.id,
      contractDate: payload.startDate,
      startAddress: payload.address,
      sector: matchedSector.name,
      billingDocumentType: payload.billingDocumentType,
    });

    if (editingRow.meterId) {
      await updateMeter(session, editingRow.meterId, {
        serialNumber: payload.meterNumber || undefined,
        sealNumber: payload.sealNumber || undefined,
        meterType: payload.meterType || undefined,
      });
    }

    await changeContractStatus(session, editingRow.id, {
      toStatus: payload.status === "vigente" ? "active" : "inactive",
      reason: "Actualización manual desde administración",
    });

    await loadRows();
  }

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const matchSearch =
        search === "" ||
        row.cliente.toLowerCase().includes(search.toLowerCase()) ||
        row.rut.toLowerCase().includes(search.toLowerCase()) ||
        row.contractNumber.toLowerCase().includes(search.toLowerCase()) ||
        row.medidor.toLowerCase().includes(search.toLowerCase());
      const matchEstado = estadoFilter === "todos" || row.status === estadoFilter;
      const matchTipo = tipoFilter === "todos" || row.customerType === tipoFilter;
      return matchSearch && matchEstado && matchTipo;
    });
  }, [rows, search, estadoFilter, tipoFilter]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      vigentes: rows.filter((row) => row.status === "active").length,
      inactivos: rows.filter((row) => row.status === "inactive").length,
      comerciales: rows.filter((row) => row.customerType === "empresa").length,
    }),
    [rows],
  );

  const estadoFilters: Array<{ key: EstadoFilter; label: string }> = [
    { key: "todos", label: "Todos" },
    { key: "active", label: "Vigentes" },
    { key: "inactive", label: "Inactivos" },
  ];

  const tipoFilters: Array<{ key: TipoFilter; label: string }> = [
    { key: "todos", label: "Todos los tipos" },
    { key: "domiciliario", label: "Residencial" },
    { key: "empresa", label: "Comercial" },
    { key: "institucion", label: "Institucional" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/administracion")}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Contratos</h1>
            <p className="text-sm text-gray-500 mt-0.5">Información real de contratos creados en módulo Clientes</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/clientes")}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          Nuevo contrato
        </button>
      </div>

      {error ? <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div> : null}

      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total contratos" value={stats.total} icon="ri-file-text-line" color="text-gray-600" bg="bg-gray-100" />
        <StatCard label="Vigentes" value={stats.vigentes} icon="ri-checkbox-circle-line" color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard label="Inactivos" value={stats.inactivos} icon="ri-close-circle-line" color="text-gray-600" bg="bg-gray-100" />
        <StatCard label="Comerciales" value={stats.comerciales} icon="ri-store-2-line" color="text-violet-600" bg="bg-violet-50" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Buscar por cliente, RUT, N° contrato o medidor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
            />
          </div>
          <select
            value={tipoFilter}
            onChange={(e) => setTipoFilter(e.target.value as TipoFilter)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 bg-white"
          >
            {tipoFilters.map((filter) => (
              <option key={filter.key} value={filter.key}>
                {filter.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 mt-3">
          {estadoFilters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setEstadoFilter(filter.key)}
              className={`px-3 py-1 text-xs font-medium rounded-full ${
                estadoFilter === filter.key ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {filter.label}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-400">{filtered.length} resultados</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">N° Contrato</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Cliente</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Tipo</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Sector</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Medidor</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Tarifa</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Estado</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Fecha alta</th>
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="text-center py-12 text-sm text-gray-400">Cargando contratos...</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-12 text-sm text-gray-400">No se encontraron contratos</td>
              </tr>
            ) : (
              filtered.map((row, index) => (
                <tr key={row.id} className={`border-b border-gray-50 ${index === filtered.length - 1 ? "border-b-0" : ""}`}>
                  <td className="px-5 py-3.5 text-xs font-mono text-gray-600">{row.contractNumber}</td>
                  <td className="px-5 py-3.5">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{row.cliente}</p>
                      <p className="text-xs text-gray-400">{row.rut}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-xs font-medium text-gray-600">{tipoLabel[row.customerType]}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-600">{row.sector}</td>
                  <td className="px-5 py-3.5 text-xs font-mono text-gray-500">{row.medidor}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-600">{row.tariffName}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${estadoBadge[row.status] ?? "bg-gray-100 text-gray-600"}`}>
                      {estadoLabel[row.status] ?? row.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-600">{new Date(row.contractDate).toLocaleDateString("es-CL")}</td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => setEditingRow(row)}
                      className="px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg"
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editingRow ? (
        <NuevoContratoModal
          cliente={{
            id: editingRow.id,
            nombre: editingRow.cliente,
            rut: editingRow.rut,
            direccion: editingRow.direccion,
            sector: editingRow.sector,
            customerType: editingRow.customerType,
          }}
          tariffOptions={tariffOptions}
          sectorOptions={sectorOptions}
          mode="edit"
          initialValues={{
            address: editingRow.direccion,
            startDate: editingRow.contractDate.slice(0, 10),
            sectorId: editingRow.sectorId || sectorOptions.find((s) => s.name === editingRow.sector)?.id || sectorOptions[0]?.id || "",
            tariffId: tariffOptions.find((t) => t.name === editingRow.tariffName)?.id || tariffOptions[0]?.id || "",
            meterType: editingRow.tipoMedidor,
            meterNumber: editingRow.medidor,
            sealNumber: editingRow.selloMedidor,
            status: editingRow.status === "active" ? "vigente" : "suspendido",
            billingDocumentType: editingRow.billingDocumentType,
          }}
          onClose={() => setEditingRow(null)}
          onCreated={handleEditContract}
        />
      ) : null}
    </div>
  );
}

function StatCard(props: { label: string; value: number; icon: string; color: string; bg: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${props.bg}`}>
          <i className={`${props.icon} ${props.color}`} />
        </div>
        <div>
          <p className="text-xs text-gray-400">{props.label}</p>
          <p className="text-xl font-bold text-gray-800">{props.value}</p>
        </div>
      </div>
    </div>
  );
}
